import Navigation from '../../../components/feature/Navigation';
import Footer from '../../../components/feature/Footer';
import { Link } from 'react-router-dom';

const TechnologyPage = () => {
  const capabilities = [
    {
      title: 'Managed IT Advisory',
      description: 'Strategic guidance on IT infrastructure, vendor selection, and technology roadmap development.',
      icon: 'ri-settings-3-line',
    },
    {
      title: 'Cybersecurity & Compliance',
      description: 'Risk assessment, security architecture, and regulatory compliance frameworks.',
      icon: 'ri-shield-check-line',
    },
    {
      title: 'Cloud, Data & Infrastructure',
      description: 'Cloud migration strategy, data architecture, and infrastructure optimization.',
      icon: 'ri-cloud-line',
    },
    {
      title: 'Software, Web & App Development',
      description: 'Custom software solutions, web applications, and mobile app development.',
      icon: 'ri-smartphone-line',
    },
    {
      title: 'AI & Automation',
      description: 'Artificial intelligence integration, process automation, and machine learning solutions.',
      icon: 'ri-robot-line',
    },
  ];

  return (
    <div className="min-h-screen bg-black">
      <Navigation />
      
      <main className="pt-[90px]">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-b from-[#0A0A0A] to-black py-32">
          <div className="max-w-[1400px] mx-auto px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div>
                <div className="inline-flex items-center px-4 py-2 border border-copper/30 rounded-full mb-8">
                  <span className="text-xs uppercase tracking-wider text-copper">Technology Services</span>
                </div>
                <h1 className="text-6xl lg:text-7xl font-bold text-white mb-8 leading-tight">
                  Technology &{' '}
                  <span className="bg-copper-gradient bg-clip-text text-transparent">
                    Digital Services
                  </span>
                </h1>
                <p className="text-xl text-gray-400 leading-relaxed mb-10">
                  Comprehensive IT advisory, cybersecurity, cloud infrastructure, and digital transformation solutions for modern enterprises seeking competitive advantage through technology.
                </p>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-copper hover:bg-copper-light text-white px-10 py-4 rounded-full text-base font-semibold transition-all duration-300 whitespace-nowrap"
                >
                  Schedule Consultation
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
              <div className="relative h-[500px] rounded-2xl overflow-hidden">
                <img
                  src="https://public.readdy.ai/ai/img_res/f11ecf449fb779613d205d548535d63d.jpg"
                  alt="Technology Services"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              </div>
            </div>
          </div>
        </section>

        {/* Capabilities Grid */}
        <section className="bg-charcoal-dark py-24">
          <div className="max-w-[1400px] mx-auto px-8">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-white mb-4">Our Technology Capabilities</h2>
              <p className="text-lg text-gray-400 max-w-3xl mx-auto">
                End-to-end technology consulting services designed to modernize operations and drive digital transformation.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {capabilities.map((capability, index) => (
                <div
                  key={index}
                  className="bg-charcoal border border-charcoal-light rounded-2xl p-8 hover:border-copper transition-all duration-300"
                >
                  <div className="w-16 h-16 bg-copper/10 rounded-full flex items-center justify-center mb-6">
                    <i className={`${capability.icon} text-4xl text-copper`}></i>
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-4">{capability.title}</h3>
                  <p className="text-base text-gray-400 leading-relaxed">{capability.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Approach Section */}
        <section className="bg-black py-24">
          <div className="max-w-[1400px] mx-auto px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <div className="relative h-[400px] rounded-2xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=professional%20technology%20consultants%20collaborating%20on%20digital%20transformation%20strategy%20with%20laptops%20and%20documents%20in%20modern%20office%2C%20business%20technology%20planning%20session%20with%20sophisticated%20corporate%20atmosphere&width=700&height=400&seq=tech-approach-002&orientation=landscape"
                  alt="Our Approach"
                  className="w-full h-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
              </div>
              <div>
                <h2 className="text-4xl font-bold text-white mb-6">Our Approach</h2>
                <p className="text-lg text-gray-400 leading-relaxed mb-6">
                  We combine strategic advisory with hands-on implementation support to ensure technology initiatives deliver measurable business value. Our methodology focuses on alignment with business objectives, risk mitigation, and sustainable outcomes.
                </p>
                <ul className="space-y-4">
                  {[
                    'Comprehensive technology assessment and gap analysis',
                    'Strategic roadmap development aligned with business goals',
                    'Vendor selection and contract negotiation support',
                    'Implementation oversight and change management',
                    'Ongoing optimization and continuous improvement',
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <i className="ri-check-line text-copper text-xl mt-1"></i>
                      <span className="text-base text-gray-300">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-charcoal-dark py-24">
          <div className="max-w-[1400px] mx-auto px-8 text-center">
            <h2 className="text-5xl font-bold text-white mb-6">
              Transform Your Technology Infrastructure
            </h2>
            <p className="text-xl text-gray-400 mb-10 max-w-2xl mx-auto">
              Partner with MACGOC to modernize your IT operations and drive digital innovation.
            </p>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-copper hover:bg-copper-light text-white px-12 py-4 rounded-full text-lg font-semibold transition-all duration-300 whitespace-nowrap"
            >
              Get Started
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default TechnologyPage;